import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { AlertTriangle, Loader2, Plus, RefreshCw, Rocket } from "lucide-react";
import PartnerPageHeader from "../components/PartnerPageHeader";
import ClientRosterTable from "../components/ClientRosterTable";
import ChangeTierModal from "../components/ChangeTierModal";
import SetRetailModal from "../components/SetRetailModal";
import StopBillingModal from "../components/StopBillingModal";
import ResumeBillingModal from "../components/ResumeBillingModal";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { setImpersonationCookies } from "@/lib/authCookies";
import { getAppOrigin } from "@/lib/siteHosts";
import { DASHBOARD_ROUTES } from "@/lib/dashboardRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "N/A";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatRelativeTime = (isoDate) => {
  if (!isoDate) return "N/A";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "N/A";

  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(isoDate);
};

const mapRosterRow = (row) => ({
  id: row.id,
  name: row.name,
  email: row.email,
  vertical: (row.vertical || "").replace(/_/g, " "),
  tier: row.tier,
  creditsUsed: row.credits_used,
  creditsIncluded: row.credits_included,
  status: row.status,
  createdAt: formatDate(row.created_at),
  lastActivity: formatRelativeTime(row.last_activity_at),
  billingStopAt: row.billing_stop_at || null,
  partnerRetailMonthlyUsd: row.partner_retail_monthly_usd ?? null,
  retailMonthlyUsd: row.retail_monthly_usd,
  marginMonthlyUsd: row.margin_monthly_usd,
  retailSource: row.retail_source,
});

const RosterSkeleton = () => (
  <div
    className="rounded-2xl border border-partner bg-[#11151F] p-8 animate-pulse"
    data-testid="partner-clients-skeleton"
  >
    <div className="h-10 bg-white/5 rounded mb-6" />
    <div className="space-y-3">
      <div className="h-12 bg-white/5 rounded" />
      <div className="h-12 bg-white/5 rounded" />
      <div className="h-12 bg-white/5 rounded" />
    </div>
  </div>
);

const PartnerClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tierChangeClient, setTierChangeClient] = useState(null);
  const [stopBillingClient, setStopBillingClient] = useState(null);
  const [restoreBilling, setRestoreBilling] = useState(null);
  const [retailModalClient, setRetailModalClient] = useState(null);
  const [openingConsoleClientId, setOpeningConsoleClientId] = useState(null);
  const [consoleError, setConsoleError] = useState("");

  const getAuthHeader = useCallback(() => getPartnerAuthHeaders(), []);

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/partner/clients`, {
        headers: getAuthHeader(),
      });
      const rows = res.data?.clients || [];
      setClients(rows.map(mapRosterRow));
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setLoadError(
        extractErrorMessage(err, "Could not load your client roster. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, navigate]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const isEmptyRoster = useMemo(
    () => !isLoading && !loadError && clients.length === 0,
    [isLoading, loadError, clients.length]
  );

  const handleTierChanged = (row) => {
    const mapped = mapRosterRow(row);
    setClients((prev) =>
      prev.map((c) => (c.id === mapped.id ? { ...c, ...mapped } : c))
    );
  };

  const handleRetailChanged = handleTierChanged;

  const handleStopBillingScheduled = handleTierChanged;
  const handleRestoreBillingSuccess = handleTierChanged;

  const openResumeBilling = (client) => setRestoreBilling({ client, mode: "resume" });
  const openReactivate = (client) => setRestoreBilling({ client, mode: "reactivate" });

  const handleOpenConsole = async (client) => {
    if (!client?.id) return;
    setConsoleError("");
    setOpeningConsoleClientId(client.id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/partner/clients/${client.id}/impersonate`,
        {},
        { headers: getAuthHeader() }
      );
      const {
        impersonation_token: impersonationToken,
        redirect_path: redirectPath,
        client: clientMeta,
      } = res.data || {};

      if (!impersonationToken) {
        throw new Error("Impersonation token missing from server response");
      }

      setImpersonationCookies({
        token: impersonationToken,
        botType: clientMeta?.bot_type,
        clientName: clientMeta?.name || client.name,
      });

      const origin = (getAppOrigin() || window.location.origin).replace(/\/$/, "");
      const path = redirectPath || DASHBOARD_ROUTES.settings;
      const url = `${origin}${path.startsWith("/") ? path : `/${path}`}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setConsoleError(
        extractErrorMessage(err, "Could not open client console. Please try again.")
      );
    } finally {
      setOpeningConsoleClientId(null);
    }
  };

  return (
    <div data-testid="partner-clients">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <PartnerPageHeader
          tag="Client portfolio"
          title="Client"
          titleAccent="roster"
          subtitle="Manage deployed Digital Assistants under your brand. Open a client console to configure Digital Brain settings on their behalf."
        />
        <Link
          to="/partner/deploy"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00D2FF]/10 text-partner-accent border border-[#00D2FF]/30 font-body text-sm font-semibold hover:bg-[#00D2FF]/15 transition-all flex-shrink-0 self-start"
        >
          <Plus className="w-4 h-4" />
          Deploy client
        </Link>
      </div>

      {isLoading && <RosterSkeleton />}

      {!isLoading && consoleError && (
        <div
          className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4 mb-6 flex items-start gap-3"
          data-testid="partner-open-console-error"
        >
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-partner-muted font-body">{consoleError}</p>
        </div>
      )}

      {!isLoading && loadError && (
        <div
          className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center mb-6"
          data-testid="partner-clients-load-error"
        >
          <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-4" />
          <p className="text-sm text-partner-muted font-body max-w-md mx-auto mb-4">{loadError}</p>
          <button
            type="button"
            onClick={fetchClients}
            className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-white/10 text-partner hover:bg-white/20"
            data-testid="partner-clients-retry-button"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      )}

      {isEmptyRoster && (
        <div
          className="rounded-2xl border border-partner bg-[#11151F] p-10 md:p-14 text-center"
          data-testid="partner-clients-empty"
        >
          <Rocket className="mx-auto h-10 w-10 text-partner-accent mb-4" />
          <h2 className="text-xl font-bold text-partner mb-2">No clients deployed yet</h2>
          <p className="text-sm text-partner-muted font-body max-w-md mx-auto mb-6">
            Deploy your first Digital Assistant to start building your client roster. Wholesale billing
            applies automatically when you deploy.
          </p>
          <Link
            to="/partner/deploy"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Deploy client
          </Link>
        </div>
      )}

      {!isLoading && !loadError && clients.length > 0 && (
        <ClientRosterTable
          clients={clients}
          isEmptyRoster={false}
          onChangeTier={setTierChangeClient}
          onStopBilling={setStopBillingClient}
          onResumeBilling={openResumeBilling}
          onReactivate={openReactivate}
          onOpenConsole={handleOpenConsole}
          onSetRetail={setRetailModalClient}
          openingConsoleClientId={openingConsoleClientId}
        />
      )}

      <ChangeTierModal
        client={tierChangeClient}
        open={tierChangeClient !== null}
        onClose={() => setTierChangeClient(null)}
        onSuccess={handleTierChanged}
      />

      <SetRetailModal
        client={retailModalClient}
        open={retailModalClient !== null}
        onClose={() => setRetailModalClient(null)}
        onSuccess={handleRetailChanged}
      />

      <StopBillingModal
        client={stopBillingClient}
        open={stopBillingClient !== null}
        onClose={() => setStopBillingClient(null)}
        onSuccess={handleStopBillingScheduled}
      />

      <ResumeBillingModal
        client={restoreBilling?.client ?? null}
        mode={restoreBilling?.mode ?? null}
        open={restoreBilling !== null}
        onClose={() => setRestoreBilling(null)}
        onSuccess={handleRestoreBillingSuccess}
      />
    </div>
  );
};

export default PartnerClients;
